import { useState } from 'react'
import { useRecoilState, useResetRecoilState } from 'recoil'
import { Flex } from '@/components/priority/Flex/Flex'
import { ToggleSwitch } from '@/components/global/ToggleSwitch/ToggleSwitch'
import { MenuDrawer } from '@/components/editor/MenuDrawer/MenuDrawer'
import { SnapToGuidesDrawer } from '@/components/editor/PropertiesMenu/Drawers/SnapToGuidesDrawer'
import { QuickActionsDrawer } from '@/components/editor/PropertiesMenu/Drawers/QuickActionsDrawer'
import { EditorTextBoxTurnoverControl } from '@/components/editor/PropertiesMenu/EditorTextBoxTurnoverControl'
import {
  showRulerAtom,
  insetRulerAtom,
  outsideCanvasItemsVisibleAtom,
  showProductBleedAtom,
  itemsLockedAtom,
  selectedItemsAtom,
} from 'atoms'
import { useIsAdmin } from '@/hooks/utility/useIsAdmin'
import { useEditorLotusEnabled } from '@/hooks/editor/useEditorLotusEnabled'
import { useEditorLotusOffered } from '@/hooks/editor/useEditorLotusOffered'
import { useTranslation } from 'react-i18next'

export const SettingsMenu = () => {
  const { t } = useTranslation()
  const [showRuler, setShowRuler] = useRecoilState(showRulerAtom)
  const [lockItems, setLockItems] = useRecoilState(itemsLockedAtom)
  const [showProductBleed, setShowProductBleed] =
    useRecoilState(showProductBleedAtom)
  const [insetRuler, setInsetRuler] = useRecoilState(insetRulerAtom)
  const [outsideCanvasItemsVisible, setOutsideCanvasItemsVisible] =
    useRecoilState(outsideCanvasItemsVisibleAtom)
  const resetSelectedItemsAtom = useResetRecoilState(selectedItemsAtom)
  const [snapGuidesDrawerOpen, setSnapGuidesDrawerOpen] = useState(false)
  const [quickActionsDrawerOpen, setQuickActionsDrawerOpen] = useState(false)
  const [lotusEnabled, setLotusEnabled] = useEditorLotusEnabled()

  const isAdmin = useIsAdmin()
  const lotusOffered = useEditorLotusOffered()

  const _lockItems = (toggledLockItemsState) => {
    setLockItems(toggledLockItemsState)
    resetSelectedItemsAtom()
  }

  return (
    <Flex column>
      <Flex column gap="32px" padding="0px 24px" margin="24px 0">
        <Flex column gap="12px" data-testid="editor-settings-toggles">
          <ToggleSwitch
            value={[showRuler, setShowRuler]}
            label={t('editor.menus.properties.settings.showRulerLabel')}
            labelRight
          />
          <ToggleSwitch
            value={[insetRuler, setInsetRuler]}
            label={t('editor.menus.properties.settings.insetRulerLabel')}
            labelRight
            disabled={!showRuler}
          />
          <ToggleSwitch
            value={[outsideCanvasItemsVisible, setOutsideCanvasItemsVisible]}
            label={t('editor.menus.properties.settings.showCanvasLabel')}
            labelRight
          />
          <ToggleSwitch
            value={[showProductBleed, setShowProductBleed]}
            label={t('editor.menus.properties.settings.showMarginsLabel')}
            labelRight
          />
          <ToggleSwitch
            value={[lockItems, _lockItems]}
            label={t('editor.menus.properties.settings.lockItemsLabel')}
            labelRight
            data-testid="editor-settings-lock-toggle"
            data-editor-items-locked={lockItems ? 'true' : 'false'}
          />
          {/* Only internal staff see this switch, and only where Lotus text
              boxes are offered for the open order's funeral home. The state
              attribute is what the e2e proof reads — the switch renders no
              form control to inspect. */}
          {lotusOffered && (
            <ToggleSwitch
              value={[lotusEnabled, setLotusEnabled]}
              label={t('editor.menus.properties.settings.newTextBoxesLabel')}
              labelRight
              data-testid="editor-settings-lotus-toggle"
              data-editor-lotus-enabled={lotusEnabled ? 'true' : 'false'}
            />
          )}
        </Flex>
      </Flex>
      {/* Outside the toggles, because that list holds only switches. Shows
          to internal staff only, where Lotus is offered. */}
      <EditorTextBoxTurnoverControl />
      {isAdmin && (
        <MenuDrawer
          show={[
            snapGuidesDrawerOpen,
            () => setSnapGuidesDrawerOpen(!snapGuidesDrawerOpen),
          ]}
          title={t('editor.menus.properties.settings.snapToGuides.title')}
          startOpen={false}
          data-testid="editor-settings-snap-guides-drawer"
        >
          <SnapToGuidesDrawer />
        </MenuDrawer>
      )}
      <MenuDrawer
        show={[
          quickActionsDrawerOpen,
          () => setQuickActionsDrawerOpen(!quickActionsDrawerOpen),
        ]}
        title={t('editor.menus.properties.elements.bulkQuickActions.title')}
        startOpen={false}
      >
        <QuickActionsDrawer />
      </MenuDrawer>
    </Flex>
  )
}

export default SettingsMenu
